import { useAppToast } from "@/hooks/useAppToast";
import { getPageRoutes } from "@/routes/pageRoutes";
import { useGetNowShowingQuery } from "@/store/services/movie";
import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Image,
  ImageProps,
  Text,
  TextProps,
} from "@chakra-ui/react";
import { SelectField } from "@picovico/ui";
import { getLocalStorageData } from "@picovico/utils";
import { format, isSameDay, isToday, isTomorrow } from "date-fns";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import BookingFormSkeleton from "./bookingFormSkeleton";

interface BookingFormData {
  movie: string;
  date: string;
}

export interface BookingFormWithDetailProps {
  movieImageProps?: ImageProps;
  titleProps?: TextProps;
  selectContainerProps?: BoxProps;
  // Per-slot style overrides for chakra-react-select internals (control, singleValue,
  // placeholder, input, menu, option). Values are plain objects merged after the
  // component's own base styles, so they always win without replacing them entirely.
  selectChakraStyles?: Record<string, Record<string, unknown>>;
  variant?: string;
  labelProps?: TextProps;
  formFieldBoxProps?: FlexProps;
  formContainerWrapperProps?: FlexProps;
  formSectionProps?: FlexProps;
  posterContainerProps?: FlexProps;
  posterBoxProps?: FlexProps;
  movieRollQualityBoxProps?: FlexProps;
  movieGradeBoxProps?: FlexProps;
  formContainerProps?: FlexProps;
  formCardHeaderProps?: BoxProps;
  showTmeBoxProps?: BoxProps;
  showTimeTitleProps?: BoxProps;
  showTimeListBoxProps?: FlexProps;
  showTimeItemProps?: BoxProps;
  // Config-driven colours for show-time buttons; keeps expired/active distinction intact
  showTimeActiveColor?: string;
  showTimeExpiredColor?: string;
  showTimeAuditNameProps?: TextProps;
  showNotAvailableBoxProps?: BoxProps;
  showNotAvailableTextProps?: TextProps;
  showTimeTextProps?: TextProps;
  summaryPanelContainerWrapperProps?: FlexProps;
  summaryPanelContainerProps?: FlexProps;
  summaryPanelTitleProps?: TextProps;
  summaryDetailBoxProps?: BoxProps;
  summaryDetailTextProps?: TextProps;
  summaryDetailRowProps?: FlexProps;
  summaryDetailLabelProps?: TextProps;
}

const BookingFormWithDetail = ({
  movieImageProps,
  titleProps,
  selectContainerProps,
  selectChakraStyles,
  labelProps,
  formFieldBoxProps,
  formContainerWrapperProps,
  formSectionProps,
  posterContainerProps,
  posterBoxProps,
  movieRollQualityBoxProps,
  movieGradeBoxProps,
  formContainerProps,
  formCardHeaderProps,
  showTmeBoxProps,
  showTimeTitleProps,
  showTimeListBoxProps,
  showTimeItemProps,
  showTimeActiveColor,
  showTimeExpiredColor,
  showTimeAuditNameProps,
  showNotAvailableBoxProps,
  showNotAvailableTextProps,
  showTimeTextProps,
  summaryPanelContainerWrapperProps,
  summaryPanelContainerProps,
  summaryPanelTitleProps,
  summaryDetailBoxProps,
  summaryDetailTextProps,
  summaryDetailRowProps,
  summaryDetailLabelProps,
}: BookingFormWithDetailProps) => {
  const [formData, setFormData] = useState<BookingFormData>({
    movie: "",
    date: "",
  });
  const cinema = getLocalStorageData("cinema_hall") ?? "";
  const { warn } = useAppToast();
  const router = useRouter();

  const { data: moviesData, isLoading: isLoadingMovies } =
    useGetNowShowingQuery(
      {
        show_date: format(new Date(), "yyyy-MM-dd"),
        cinema_hall: cinema,
        advance_days: true,
      },
      {
        skip: !cinema,
      },
    );

  const movies = useMemo(() => moviesData?.data || [], [moviesData]);

  const [selectedMovie, setSelectedMovie] = useState(movies[0]);

  const movieOptions = useMemo(() => {
    return movies.map((movie) => ({
      label: movie.name,
      value: movie.idx,
    }));
  }, [movies]);

  const dateOptions = useMemo(() => {
    const uniqueDays = Array.from(
      new Set(
        selectedMovie?.showinfo?.map((show) =>
          format(show.show_start_date_time, "yyyy-MM-dd"),
        ) ?? [],
      ),
    );

    return uniqueDays.map((d) => {
      const label = isToday(d)
        ? "Today"
        : isTomorrow(d)
          ? "Tomorrow"
          : format(d, "MMM dd yyyy");

      return {
        label,
        value: d,
      };
    });
  }, [selectedMovie]);

  useEffect(() => {
    setSelectedMovie(movies[0]);
    setFormData((prev) => {
      const movieStartDate = movies[0]?.showinfo[0]?.show_start_date_time;
      return {
        ...prev,
        movie: movies[0]?.idx,
        date: movieStartDate ? format(movieStartDate, "yyyy-MM-dd") : "",
      };
    });
  }, [movies]);

  const availableShowTime = useMemo(() => {
    return (
      selectedMovie?.showinfo?.filter((show) =>
        isSameDay(show.show_start_date_time, formData.date),
      ) || []
    );
  }, [selectedMovie, formData.date]);

  const handleFieldChange = (field: keyof BookingFormData, value: string) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      if (field === "movie") {
        const movieSelected = movies.find((i) => i.idx === value);
        if (movieSelected) setSelectedMovie(movieSelected);
        const movieStartDate = movieSelected?.showinfo[0].show_end_date_time;
        newData.date = movieStartDate
          ? format(movieStartDate, "yyyy-MM-dd")
          : "";
      }
      return newData;
    });
  };

  const handleBooking = (time: string) => {
    if (!cinema || !formData.movie || !formData.date) {
      warn("Please fill all fields");
      return;
    }

    const bookingUrl = getPageRoutes("audi_detail", {
      movieIdx: formData.movie,
      showIdx: time,
    });

    router.push(bookingUrl);
  };

  if (isLoadingMovies) {
    return <BookingFormSkeleton />;
  }

  if (!movies.length) {
    return (
      <Flex justifyContent={"center"} alignItems={"center"} marginTop={"80px"}>
        <Text fontSize={"20px"}>No Show Available</Text>
      </Flex>
    );
  }

  return (
    <Flex
      mt={"35px"}
      direction={{
        base: "column",
        lg: "row",
      }}
      {...formContainerWrapperProps}
    >
      <Flex pr={"15px"} flex={1} {...formSectionProps}>
        <Box borderRadius={"20px"} width={"100%"}>
          <Box
            background={"primary.500"}
            padding={"15px 20px"}
            borderRadius={"17px 17px 0 0"}
            color={"#ffffff"}
            textTransform={"capitalize"}
            fontSize={"22px"}
            {...formCardHeaderProps}
          >
            Select Your Show
          </Box>
          <Flex
            py={"15px"}
            background={"primary.200"}
            display={"flex"}
            borderRadius={"0 0 20px 20px"}
            direction={{
              base: "column",
              "2sm": "row",
            }}
            {...posterContainerProps}
          >
            <Flex
              flex={1}
              px={"15px"}
              position={"relative"}
              marginBottom={"20px"}
              {...posterBoxProps}
            >
              <Image
                sx={{
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
                w="full"
                itemProp="image"
                itemScope
                itemRef="imageRef"
                objectFit="cover"
                src={selectedMovie?.poster}
                alt={selectedMovie?.name}
                {...movieImageProps}
              />
              <Flex
                justifyContent={"center"}
                alignItems={"center"}
                top={2}
                left={6}
                borderRadius={"50%"}
                background={"#000000A7"}
                w={"40px"}
                height={"40px"}
                position={"absolute"}
                color={"#ffffff"}
                {...movieRollQualityBoxProps}
              >
                {selectedMovie?.roll_quality}
              </Flex>
              <Flex
                justifyContent={"center"}
                alignItems={"center"}
                top={2}
                right={6}
                borderRadius={"50%"}
                background={"#000000A7"}
                w={"40px"}
                height={"40px"}
                position={"absolute"}
                color={"#ffffff"}
                {...movieGradeBoxProps}
              >
                {selectedMovie?.grade}
              </Flex>
            </Flex>
            <Flex
              flex={2}
              px={"15px"}
              direction={"column"}
              {...formContainerProps}
            >
              <Text
                fontSize="18px"
                fontWeight="bold"
                color="#000000"
                mb={"10px"}
                textTransform={"capitalize"}
                {...titleProps}
              >
                Select Movie
              </Text>
              <Flex
                justifyContent={"center"}
                alignItems={"center"}
                borderRadius={"10px"}
                background={"#ffffffA5"}
                mb={"15px"}
                _hover={{
                  background: "#ffffff",
                }}
                {...formFieldBoxProps}
              >
                <Flex
                  background={"primary.500"}
                  fontSize="15px"
                  fontWeight="bold"
                  textAlign="center"
                  color="#ffffff"
                  padding={"10px 15px"}
                  width={"25%"}
                  borderRadius={"10px 0 0 10px"}
                  {...labelProps}
                >
                  Movie:
                </Flex>
                <Box flex={1} marginRight={"10px"} {...selectContainerProps}>
                  <SelectField
                    placeholder="Choose movie..."
                    options={movieOptions}
                    value={
                      formData.movie && {
                        label:
                          movies.find((m) => m.idx === formData.movie)?.name ||
                          "",
                        value: formData.movie,
                      }
                    }
                    onChange={(option) =>
                      handleFieldChange("movie", option?.value || "")
                    }
                    isDisabled={isLoadingMovies || movies.length === 0}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (provided: any) => ({
                        ...provided,
                        zIndex: 9999,
                      }),
                    }}
                    chakraStyles={{
                      control: (base) => ({
                        ...base,
                        border: "none",
                        _focus: { border: "none", boxShadow: "none" },
                        ...selectChakraStyles?.control,
                      }),
                      singleValue: (base) => ({
                        ...base,
                        ...selectChakraStyles?.singleValue,
                      }),
                      placeholder: (base) => ({
                        ...base,
                        ...selectChakraStyles?.placeholder,
                      }),
                      input: (base) => ({
                        ...base,
                        ...selectChakraStyles?.input,
                      }),
                      menu: (base) => ({
                        ...base,
                        ...selectChakraStyles?.menu,
                      }),
                      menuList: (base) => ({
                        ...base,
                        ...selectChakraStyles?.menuList,
                      }),
                      option: (base, state) => ({
                        ...base,
                        ...(typeof selectChakraStyles?.option === "function"
                          ? (selectChakraStyles.option as any)(base, state)
                          : selectChakraStyles?.option),
                      }),
                    }}
                    isRequired
                  />
                </Box>
              </Flex>
              <Flex
                justifyContent={"center"}
                alignItems={"center"}
                borderRadius={"10px"}
                mb={"15px"}
                background={"#ffffffA5"}
                _hover={{
                  background: "#ffffff",
                }}
                {...formFieldBoxProps}
              >
                <Flex
                  background={"primary.500"}
                  fontSize="15px"
                  fontWeight="bold"
                  textAlign="center"
                  color="#ffffff"
                  padding={"10px 15px"}
                  width={"25%"}
                  borderRadius={"10px 0 0 10px"}
                  {...labelProps}
                >
                  Day:
                </Flex>
                <Box flex={1} marginRight={"10px"} {...selectContainerProps}>
                  <SelectField
                    placeholder="Choose movie..."
                    options={dateOptions}
                    value={
                      formData.date && {
                        label:
                          dateOptions.find((d) => d.value === formData.date)
                            ?.label || "",
                        value: formData.date,
                      }
                    }
                    onChange={(option) =>
                      handleFieldChange("date", option?.value || "")
                    }
                    isDisabled={isLoadingMovies || movies.length === 0}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (provided: any) => ({
                        ...provided,
                        zIndex: 9999,
                      }),
                    }}
                    chakraStyles={{
                      control: (base) => ({
                        ...base,
                        border: "none",
                        _focus: { border: "none", boxShadow: "none" },
                        ...selectChakraStyles?.control,
                      }),
                      singleValue: (base) => ({
                        ...base,
                        ...selectChakraStyles?.singleValue,
                      }),
                      placeholder: (base) => ({
                        ...base,
                        ...selectChakraStyles?.placeholder,
                      }),
                      input: (base) => ({
                        ...base,
                        ...selectChakraStyles?.input,
                      }),
                      menu: (base) => ({
                        ...base,
                        ...selectChakraStyles?.menu,
                      }),
                      menuList: (base) => ({
                        ...base,
                        ...selectChakraStyles?.menuList,
                      }),
                      option: (base, state) => ({
                        ...base,
                        ...(typeof selectChakraStyles?.option === "function"
                          ? (selectChakraStyles.option as any)(base, state)
                          : selectChakraStyles?.option),
                      }),
                    }}
                    isRequired
                  />
                </Box>
              </Flex>
              <Box {...showTmeBoxProps}>
                <Box {...showTimeTitleProps}>Show Time</Box>
                <Flex gap={2} flexWrap={"wrap"} {...showTimeListBoxProps}>
                  {availableShowTime.length ? (
                    availableShowTime?.map((show) => {
                      return (
                        <Box
                          background={
                            show.show_status === "expired"
                              ? (showTimeExpiredColor ?? "#797878")
                              : (showTimeActiveColor ?? "primary.500")
                          }
                          borderRadius={
                            show.show_status === "expired" ? "20px" : "10px"
                          }
                          color={"#ffffff"}
                          padding={"4px 20px"}
                          cursor={
                            show.show_status === "expired"
                              ? "not-allowed"
                              : "pointer"
                          }
                          onClick={() =>
                            show.show_status === "expired"
                              ? null
                              : handleBooking(show.show_idx)
                          }
                          {...showTimeItemProps}
                        >
                          <Text
                            fontSize={"12px"}
                            textAlign={"center"}
                            {...showTimeAuditNameProps}
                          >
                            {show.auditorium.name}
                          </Text>
                          <Text fontSize={"14px"} {...showTimeTextProps}>
                            {format(show.show_start_date_time, "hh:mm a")}
                          </Text>
                        </Box>
                      );
                    })
                  ) : (
                    <Box
                      background={"primary.500"}
                      borderRadius={"10px"}
                      color={"#ffffff"}
                      padding={"4px 20px"}
                      cursor={"pointer"}
                      {...showNotAvailableBoxProps}
                    >
                      <Text {...showNotAvailableTextProps}>
                        Show Not Available
                      </Text>
                    </Box>
                  )}
                </Flex>
              </Box>
            </Flex>
          </Flex>
        </Box>
      </Flex>
      <Flex
        pl={"20px"}
        maxWidth={{
          base: "100%",
          lg: "35%",
        }}
        flex={{
          base: "100%",
          lg: "35%",
        }}
        marginTop={{
          base: "50px",
          lg: "0px",
        }}
        {...summaryPanelContainerWrapperProps}
      >
        <Box
          borderRadius={"20px"}
          width={"100%"}
          {...summaryPanelContainerProps}
        >
          <Box
            background={"primary.500"}
            padding={"15px 20px"}
            borderRadius={"17px 17px 0 0"}
            color={"#ffffff"}
            textTransform={"capitalize"}
            fontSize={"22px"}
            {...summaryPanelTitleProps}
          >
            Your Summary Panel
          </Box>
          <Flex
            p={"15px"}
            background={"primary.200"}
            display={"flex"}
            boxShadow={"0 0px 3px #00000033"}
            direction={"column"}
            borderRadius={"0 0 20px 20px"}
            {...summaryDetailBoxProps}
          >
            <Flex flex={1} {...summaryDetailRowProps}>
              <Flex
                width={"25%"}
                pr={"20px"}
                alignItems={"center"}
                {...summaryDetailLabelProps}
              >
                Movie
              </Flex>
              <Box
                padding={"4px 20px 4px 0"}
                borderBottom={"3px solid #bfb9a6"}
                flex={1}
                {...summaryDetailTextProps}
              >
                {selectedMovie?.name}
              </Box>
            </Flex>
            <Flex flex={1} {...summaryDetailRowProps}>
              <Flex
                width={"25%"}
                pr={"20px"}
                alignItems={"center"}
                {...summaryDetailLabelProps}
              >
                Date
              </Flex>
              <Box
                padding={"4px 20px 4px 0"}
                borderBottom={"3px solid #bfb9a6"}
                flex={1}
                {...summaryDetailTextProps}
              >
                {formData?.date}
              </Box>
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
};

export default BookingFormWithDetail;
